import { useTable } from "react-table";
import { useMemo, useState } from "react";

import {
  defaultDateDisplay,
  formatCurrency,
  formatDiscountInItemName,
  formatNumberWithCommas,
  truncateString,
} from "../../../../../helpers";
import { useSelector } from "react-redux";
import { useGetUserList } from "../../../../../hooks/api/queries/useSettings";
import ToolTip from "../../../Tooltip";
import globalStyles from "../../../../../assets/styles/base/globalPage.module.scss";
import { v4 as uuidv4 } from "uuid";

const useOrderItems = ({ orderDetails, type }) => {
  const [orderItems, setOrderItems] = useState(type);
  const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const { data: userList } = useGetUserList();

  const commonColumn = useMemo(() => {
    const columns = [];

    if (orderDetails?.order_source === "INTERNAL") {
      columns.push({
        Header: "ITEM LEVEL TRACKING",
        accessor: "inventory_items",
        Cell: ({ cell: { value: inventory_items } }) => {
          if (!Array.isArray(inventory_items) || inventory_items.length === 0) {
            return (
              <span className="smallest-text-size text-gray-400">
                No tracking items
              </span>
            );
          }

          return (
            <ToolTip
              placeholderContent={inventory_items?.[0]?.item_number}
              showInfoIcon={true}
              useContentStyling={true}
              contentStyles={{
                width: "100%",
                maxWidth: "300px",
                padding: "15px",
              }}
            >
              <div
                className={`${globalStyles.rowSpaceBetween} ${globalStyles.borderBottom} pb-2`}
              >
                <p className="smallest-text-size font-bold">Tracking Number</p>
              </div>
              {inventory_items.map((item, index) => (
                <div
                  key={uuidv4()}
                  className={`${globalStyles.rowSpaceBetween} ${globalStyles.borderBottom} pt-2 pb-2`}
                >
                  <p className="smallest-text-size">{item?.item_number}</p>
                </div>
              ))}
            </ToolTip>
          );
        },
      });
    }
    columns.push({
      Header: "QUANTITY",
      accessor: "quantity",
      Cell: ({ cell: { value } }) => formatNumberWithCommas(value),
    });

    return columns;
  }, [orderDetails?.order_source]);

  const soldColumns = useMemo(
    () => [
      {
        Header: "NAME",
        accessor: "cart_item_name",
        Cell: ({ cell: { value } }) =>
          truncateString(formatDiscountInItemName(value), 50),
      },
      ...commonColumn,
      {
        Header: "UNIT",
        accessor: "product_unit",
        Cell: ({ cell: { value } }) => value || "N/A",
      },
      {
        Header: "VAT Rate (%)",
        accessor: "vat_rate",
        Cell: ({ cell: { value } }) => Number(value).toFixed(2) || "N/A",
      },
      {
        Header: "VAT",
        accessor: "item_vat_amount",
        Cell: ({ cell: { value } }) =>
          value
            ? formatCurrency({
                value,
                currencyCode: orderDetails?.store?.currency_code,
              })
            : "N/A",
      },
      {
        Header: "UNIT AMOUNT",
        accessor: "unit_price",
        Cell: ({ cell: { value } }) =>
          value
            ? formatCurrency({
                value,
                currencyCode: orderDetails?.store?.currency_code,
              })
            : "N/A",
      },
      {
        Header: "TOTAL AMOUNT",
        accessor: "total_price",
        Cell: ({ cell: { value } }) =>
          value
            ? formatCurrency({
                value,
                currencyCode: orderDetails?.store?.currency_code,
              })
            : "N/A",
      },
    ],
    [],
  );

  const returnColumns = useMemo(
    () => [
      {
        Header: "NAME",
        accessor: "cart_item",
        Cell: ({ cell: { value } }) =>
          truncateString(formatDiscountInItemName(value?.cart_item_name), 40),
      },
      ...commonColumn,
      {
        Header: "UNIT",
        accessor: "product_unit",
        Cell: ({ row }) => row?.original?.cart_item?.product_unit || "N/A",
      },
      {
        Header: "VAT Rate (%)",
        accessor: "vat_rate",
        Cell: ({ row }) =>
          Number(row?.original?.cart_item?.vat_rate).toFixed(2) || "N/A",
      },
      {
        Header: "VAT",
        accessor: "vat",
        Cell: ({ cell: { value } }) =>
          value
            ? formatCurrency({
                value,
                currencyCode: orderDetails?.store?.currency_code,
              })
            : "N/A",
      },
      {
        Header: "UNIT AMOUNT",
        accessor: "unit_price",
        Cell: ({ row }) =>
          formatCurrency({
            value: row?.original?.cart_item?.unit_price,
            currencyCode: orderDetails?.store?.currency_code,
          }),
      },
      {
        Header: "TOTAL AMOUNT",
        accessor: "total_amount",
        Cell: ({ cell: { value } }) =>
          value
            ? formatCurrency({
                value,
                currencyCode: orderDetails?.store?.currency_code,
              })
            : "N/A",
      },
      {
        Header: "STORE",
        accessor: "store",
        Cell: ({ cell: { value } }) =>
          storeList?.find((storeItem) => `${storeItem?.id}` === `${value}`)
            ?.name,
      },
      {
        Header: "INITIATOR",
        accessor: "user",
        Cell: ({ cell: { value } }) =>
          userList?.find((userId) => `${userId?.id}` === `${value}`)
            ?.first_name,
      },
      {
        Header: "DATE/TIME",
        accessor: "created_at",
        Cell: ({ cell: { value } }) => defaultDateDisplay(value),
      },
    ],
    [userList],
  );

  const instance = useTable({
    columns: orderItems === "RETURNED" ? returnColumns : soldColumns,
    data:
      orderItems === "RETURNED"
        ? orderDetails?.returned_items
        : orderDetails?.order_items || [],
  });

  return {
    instance,
    orderItems,
    setOrderItems,
  };
};

export default useOrderItems;
